import React, { useState, useEffect } from 'react';
import PreviewSpread from './preview/PreviewSpread';
import SpreadNavigation from './navigation/SpreadNavigation';
import { getSpreadDimensions } from '../../BookDesign/constants';

export default function HorizontalPreview({ book, debugMode = false, skipCover = false, continuous = false }) {
  // Always start with spread-1 when skipCover is true
  const [currentSpread, setCurrentSpread] = useState(skipCover ? 1 : 0);
  const totalPages = book?.totalPages || 0;
  const maxSpreads = Math.ceil(totalPages / 2);

  // Recalculate currentSpread if skipCover changes
  useEffect(() => {
    if (skipCover && currentSpread === 0) {
      setCurrentSpread(1);
    }
  }, [skipCover, currentSpread]);

  const spreadId = `spread-${currentSpread}`;
  const spread = book?.spreads?.[spreadId];

  // Cover PDF handling
  const coverUrl = book?.pdfUrls?.cover || null;
  const [showCover, setShowCover] = useState(false);
  
  useEffect(() => {
    // Show cover when on first spread AND cover URL exists AND skipCover is false
    setShowCover(currentSpread === 0 && coverUrl !== null && !skipCover);
    
    // Debug log
    if (debugMode) {
      console.log('HorizontalPreview data:', {
        currentSpread,
        coverUrl,
        skipCover,
        showCover: currentSpread === 0 && coverUrl !== null && !skipCover,
        book
      });
    }
  }, [currentSpread, coverUrl, debugMode, skipCover]);

  const canGoPrevious = skipCover ? currentSpread > 1 : currentSpread > 0;
  const canGoNext = currentSpread < maxSpreads - 1;

  const handleSpreadChange = (direction) => {
    if (direction === 'next' && canGoNext) {
      setCurrentSpread((prev) => prev + 1);
    } else if (direction === 'prev' && canGoPrevious) {
      setCurrentSpread((prev) => prev - 1);
    }
  };

  // Get cover color
  const frontCoverColor =
    book?.coverDetails?.frontCover?.color || book?.frontCoverColor || '#FFFFFF';

  // Continuous scrolling mode: render all interior spreads in a scrollable container
  if (continuous) {
    const start = skipCover ? 1 : 0;
    const end = maxSpreads; // inclusive of last interior spread index
    const indices = Array.from({ length: end - start + 1 }, (_, i) => start + i);

    // Intro page (single page) shown at the top when skipping cover
    const IntroPage = () => {
      const dims = getSpreadDimensions(book?.pageSize || 'us-trade');
      const pageW = dims.width;
      const pageH = dims.height;
      const scale = 0.5; // slightly larger for readability
      const title = (book?.title || 'Title').toString();
      return (
        <div className="flex justify-center w-full">
          <div
            className="preview-page"
            style={{
              position: 'relative',
              width: `${pageW * scale}px`,
              height: `${pageH * scale}px`,
              backgroundColor: 'white',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ height: 0 }} />
            <div style={{
              fontFamily: 'Baskervville, serif',
              fontSize: `${Math.max(18, Math.floor(36 * scale))}px`,
              color: '#000',
              marginTop: `${pageH * scale * 0.25}px`,
            }}>
              {title}
            </div>
            <div style={{ flex: 1 }} />
            <div style={{
              width: '100%',
              textAlign: 'center',
              marginBottom: `${100 * scale}px`,
              color: '#777',
              fontSize: `${Math.max(10, Math.floor(12 * scale))}px`,
            }}>
              <div>Created With</div>
              <div>Storybook Wizard</div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        <div
          className="rounded-lg shadow-lg overflow-y-auto"
          style={{ height: '500px', backgroundColor: '#FFFFFF' }}
        >
          <div className="py-4 flex flex-col items-center gap-6">
            {skipCover && (
              <IntroPage />
            )}
            {indices.map((idx) => (
              <div key={`spread-${idx}`} className="flex justify-center w-full">
                <PreviewSpread
                  spread={book?.spreads?.[`spread-${idx}`]}
                  book={book}
                  scale={0.4}
                  debugMode={debugMode}
                  spreadNumber={idx}
                />
              </div>
            ))}
          </div>
        </div>
        {debugMode && (
          <div className="text-xs text-gray-500 mt-2">
            <p>Continuous mode</p>
            <p>Rendered spreads: {indices.join(', ')}</p>
          </div>
        )}
      </div>
    );
  }

  // Default paged mode
  return (
    <div className="space-y-4">
      {showCover ? (
        // Show cover PDF when on the first spread and cover is available
        <div 
          className="rounded-lg shadow-lg overflow-hidden flex justify-center items-center"
          style={{ height: '500px', backgroundColor: frontCoverColor }}
        >
          <iframe 
            src={coverUrl} 
            className="w-full h-full"
            title="Book Cover"
            style={{ border: 'none' }}
          />
        </div>
      ) : (
        // Show regular spread
        <div
          className="rounded-lg shadow-lg overflow-hidden flex justify-center items-center"
          style={{
            backgroundColor: currentSpread === 0 ? frontCoverColor : '#FFFFFF',
            height: '500px'
          }}
        >
          <PreviewSpread
            spread={spread}
            book={book}
            scale={0.4}
            debugMode={debugMode}
            spreadNumber={currentSpread}
          />
        </div>
      )}

      <SpreadNavigation
        currentSpread={skipCover ? currentSpread - 1 : currentSpread}
        totalPages={skipCover ? totalPages - 2 : totalPages}
        onPrevious={() => handleSpreadChange('prev')}
        onNext={() => handleSpreadChange('next')}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
      />
      
      {debugMode && (
        <div className="text-xs text-gray-500 mt-2">
          <p>Current Spread: {currentSpread}</p>
          <p>Skip Cover: {skipCover ? 'Yes' : 'No'}</p>
          <p>Show Cover: {showCover ? 'Yes' : 'No'}</p>
          <p>Cover URL: {coverUrl ? 'Available' : 'Not available'}</p>
        </div>
      )}
    </div>
  );
}
